import React from "react";
import {
  addDelivery,
  setNextBatter,
  setNextBowler,
  endInnings,
  startNextInnings,
  getMatchResult,
  currentInning,
  getCurrentRunRate,
  getRequiredRunRate,
  getOversDisplay,
  getPartnership,
  getProjectedScore,
  getBallsPerOver,
  undoLastDelivery,
  applyToss,
  retireHurtBatter,
  returnRetiredBatter,
  adminRestoreBatter,
  getBatterStatus,
} from "../engine/MatchEngine";
import { MatchState, Player, DismissalType, Inning, Delivery } from "../matchTypes";
import EnhancedScoringEffects, { EffectType } from "../components/EnhancedScoringEffects";
import UndoOverModal from "../components/UndoOverModal";
import Watermark from "../components/Watermark";
import { soundManager } from "../utils/soundManager";
import { playSixSound, playFourSound, playWicketSound } from "../utils/soundEffects";
import { playSixSfx, playFourSfx, playWicketSfx } from "../services/sfx";
import { useToast } from "../components/Toast";
import { formatPlayerName } from "../utils/nameFormatter";
import { generateDeliveryCommentary } from "../utils/commentaryGenerator";
import CurrentPlayersCard from "../components/broadcast/CurrentPlayersCard";
import ScoringKeypad from "../components/broadcast/ScoringKeypad";
import TeamLogo from "../components/broadcast/TeamLogo";
import InlinePlayerSelector from "../components/InlinePlayerSelector";
import InningsCompleteModal from "../components/broadcast/InningsCompleteModal";
import MatchCompleteModal from "../components/broadcast/MatchCompleteModal";
import { getBowlerStats } from "../engine/playerStats";
import { useAuth } from "../contexts/AuthContext";
import { supabase } from "../supabaseClient";

function getCurrentOverDeliveries(inning: Inning, ballsPerOver: number): Delivery[] {
  const deliveries = inning.deliveries;
  if (deliveries.length === 0) return [];
  const totalLegalBalls = deliveries.filter(d => d.isLegal).length;
  if (totalLegalBalls === 0) return deliveries.slice();
  const legalInCurrentOver = totalLegalBalls % ballsPerOver;
  const legalBeforeCurrentOver = totalLegalBalls - (legalInCurrentOver === 0 ? ballsPerOver : legalInCurrentOver);
  let legalSeen = 0;
  let firstLegalIdx = -1;
  for (let i = 0; i < deliveries.length; i++) {
    if (deliveries[i].isLegal) {
      legalSeen++;
      if (legalSeen === legalBeforeCurrentOver + 1) {
        firstLegalIdx = i;
        break;
      }
    }
  }
  if (firstLegalIdx === -1) return [];
  let startIdx = firstLegalIdx;
  while (startIdx > 0 && !deliveries[startIdx - 1].isLegal) {
    startIdx--;
  }
  return deliveries.slice(startIdx);
}

function deliveryChipClass(d: Delivery): string {
  const base = "inline-flex items-center justify-center min-w-[1.75rem] h-7 px-1.5 rounded-md text-xs font-bold ";
  if (d.isWicket && d.isLegal) return base + "bg-red-500/90 text-white";
  if (d.extraType === "wide") return base + "bg-amber-500/90 text-black";
  if (d.extraType === "noball") return base + "bg-orange-500/90 text-black";
  if (d.wallBonus) return base + "bg-purple-500/90 text-white";
  if (d.runs === 4) return base + "bg-blue-500/90 text-white";
  if (d.runs === 6) return base + "bg-emerald-500/90 text-white";
  if (d.extraType === "bye" || d.extraType === "legbye") return base + "bg-slate-500/90 text-white";
  return base + "bg-slate-200 text-slate-900 border border-slate-400";
}

interface Props {
  state: MatchState;
  setState: (s: MatchState) => void;
  isPro?: boolean;
  onMatchComplete?: () => void;
  onViewSummary?: () => void;
  onBackToMatches?: () => void;
}

const LiveScore: React.FC<Props> = ({ state, setState, isPro = false, onMatchComplete, onViewSummary, onBackToMatches }) => {
  const inning = currentInning(state);
  const isIndoor = state.format === "INDOOR";
  const showProFeatures = isIndoor || isPro;
  const { showToast } = useToast();
  const { user } = useAuth();
  const [commentaryText, setCommentaryText] = React.useState("");
  const [showDismissalModal, setShowDismissalModal] = React.useState(false);
  const [dismissalType, setDismissalType] = React.useState<DismissalType | "">("");
  const [selectedFielderId, setSelectedFielderId] = React.useState<string>("");
  const [runOutBatterId, setRunOutBatterId] = React.useState<string>("");
  const [battersCrossed, setBattersCrossed] = React.useState(false);
  const [tossWinner, setTossWinner] = React.useState<"A" | "B" | "">("");
  const [tossDecision, setTossDecision] = React.useState<"BAT" | "BOWL" | "">("");
  const [scoringEffect, setScoringEffect] = React.useState<EffectType | null>(null);
  const [enableVisualEffects, setEnableVisualEffects] = React.useState(true);

  const [pendingExtraType, setPendingExtraType] = React.useState<"wide" | "noball" | "bye" | "legbye" | null>(null);
  const [pendingRuns, setPendingRuns] = React.useState<number | null>(null);
  const [pendingWallBonus, setPendingWallBonus] = React.useState(false);

  const [lastOverSnapshot, setLastOverSnapshot] = React.useState<MatchState | null>(null);
  const [showUndoOverModal, setShowUndoOverModal] = React.useState(false);

  // New UX state flags
  const [waitingForNewBatsman, setWaitingForNewBatsman] = React.useState(false);
  const [waitingForNewBowler, setWaitingForNewBowler] = React.useState(false);
  const [showPlayerSelector, setShowPlayerSelector] = React.useState<"batsman" | "nonstriker" | "bowler" | null>(null);
  const [showBatterManager, setShowBatterManager] = React.useState(false);
  const [showRetireConfirm, setShowRetireConfirm] = React.useState(false);
  const [retirePlayerId, setRetirePlayerId] = React.useState<string | null>(null);
  const [retirePlayerName, setRetirePlayerName] = React.useState<string>("");
  const [pendingPlayerChange, setPendingPlayerChange] = React.useState<{
    role: "striker" | "nonstriker" | "bowler";
    previousPlayerId: string | null;
    previousPlayerName: string;
  } | null>(null);
  const [prePlayerChangeSnapshot, setPrePlayerChangeSnapshot] = React.useState<MatchState | null>(null);
  const [incomingBatterSlot, setIncomingBatterSlot] = React.useState<"striker" | "nonstriker" | null>(null);

  // Anti-double-tap protection & feedback
  const [scoringButtonsDisabled, setScoringButtonsDisabled] = React.useState(false);
  const [scoringConfirmation, setScoringConfirmation] = React.useState<{
    message: string;
    type: "run" | "extra" | "wicket" | "success";
    visible: boolean;
  } | null>(null);
  const confirmationTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  React.useEffect(() => {
    if (user) {
      supabase
        .from('user_settings')
        .select('enable_visual_effects')
        .eq('user_id', user.id)
        .maybeSingle()
        .then(({ data }) => {
          if (data) {
            setEnableVisualEffects(data.enable_visual_effects ?? true);
          }
        });
    }
  }, [user]);

  // Auto-prompt for non-striker when striker is set but non-striker is missing
  React.useEffect(() => {
    if (state.strikerId && !state.nonStrikerId && !showPlayerSelector && !inning.completed) {
      console.log('[DEBUG] Auto-prompt: striker set but non-striker missing. strikerId:', state.strikerId, 'showPlayerSelector:', showPlayerSelector);
    }
  }, [state.strikerId, state.nonStrikerId, showPlayerSelector, inning.completed]);

  // Track the last over boundary that has already triggered bowler selection prompt
  // This prevents re-triggering after a bowler is selected (when legalBalls is still at the boundary)
  const handledOverBoundaryRef = React.useRef<number>(-1);

  // Over-end detection
  React.useEffect(() => {
    if (import.meta.env.DEV) {
      console.log('[LiveScore UX] Over detection:', {
        legalBalls: inning.legalBalls,
        bowlerId: state.bowlerId,
        isOverComplete: inning.legalBalls > 0 && inning.legalBalls % ballsPerOver === 0,
        waitingForNewBowler,
        handledBoundary: handledOverBoundaryRef.current
      });
    }

    // Only trigger if:
    // 1. We're at an over boundary (legalBalls > 0 and divisible by 6)
    // 2. bowlerId is null (engine cleared it at over-end)
    // 3. Innings not completed
    // 4. We haven't already handled THIS specific boundary
    if (
      inning.legalBalls > 0 &&
      inning.legalBalls % ballsPerOver === 0 &&
      !state.bowlerId &&
      !inning.completed &&
      handledOverBoundaryRef.current !== inning.legalBalls
    ) {
      handledOverBoundaryRef.current = inning.legalBalls;
      if (import.meta.env.DEV) {
        console.log('[LiveScore UX] Over complete at ball', inning.legalBalls, '- setting waitingForNewBowler = true, opening selector');
      }
      setWaitingForNewBowler(true);
      setShowPlayerSelector("bowler");
      showToast("Over complete – Select new bowler", "info");
    }
  }, [inning.legalBalls, inning.completed, state.bowlerId, showToast]);

  // Helpers
  function getBattingTeam(): Player[] {
    return inning.battingTeam === "A"
      ? state.teamAPlayers
      : state.teamBPlayers;
  }

  function getBowlingTeam(): Player[] {
    return inning.bowlingTeam === "A"
      ? state.teamAPlayers
      : state.teamBPlayers;
  }

  const battingTeam = getBattingTeam();
  const bowlingTeam = getBowlingTeam();

  const bowlerStatsMap = getBowlerStats(inning, bowlingTeam);

  // Get current players
  const striker = battingTeam.find(p => p.id === state.strikerId);
  const nonStriker = battingTeam.find(p => p.id === state.nonStrikerId);
  const bowler = bowlingTeam.find(p => p.id === state.bowlerId);

  const bowlingTeamKeeperId = inning.bowlingTeam === "A" ? state.teamAKeeperId : state.teamBKeeperId;
  const bowlingTeamKeeper = bowlingTeam.find(p => p.id === bowlingTeamKeeperId);

  function handleRetireHurt(playerId: string) {
    const newState = retireHurtBatter(state, playerId);
    if (newState === state) return;
    setState(newState);
    setWaitingForNewBatsman(false);
    showToast("Batter retired hurt — available to return this innings", "info");
  }

  function handleReturnRetired(playerId: string) {
    const newState = returnRetiredBatter(state, playerId);
    if (newState === state) {
      showToast("No open batting slot — free a slot first", "warning");
      return;
    }
    setState(newState);
    setWaitingForNewBatsman(false);
    showToast("Batter resumed at the crease", "success");
  }

  function handleAdminRestore(playerId: string) {
    const newState = adminRestoreBatter(state, playerId);
    if (newState === state) return;
    setState(newState);
    showToast("Batter restored — available to bat again", "success");
  }

  const isMatchCompleted = state.status === 'completed' || state.status === 'Completed';

  // Scoring lock logic
  const isScoringLocked =
    waitingForNewBatsman ||
    waitingForNewBowler ||
    !state.strikerId ||
    !state.bowlerId;

  const canScore = Boolean(
    state.strikerId &&
    state.nonStrikerId &&
    state.bowlerId &&
    !inning.completed &&
    !isMatchCompleted &&
    !isScoringLocked
  );

  function getPlayerDisplayName(player: Player | undefined): string {
    if (!player) return "";
    let displayName = formatPlayerName(player.name);
    if (player.isCaptain && player.isKeeper) {
      displayName += " (C) (WK)";
    } else if (player.isCaptain) {
      displayName += " (C)";
    } else if (player.isKeeper) {
      displayName += " (WK)";
    }
    return displayName;
  }

  function handleDismissal() {
    if (!dismissalType) return;

    // Validation: dismissals that require a legal delivery cannot occur on a
    // no-ball. Run-out (and any run-based dismissal) is the only exception.
    // On a wide, Stumped and Hit Wicket remain valid (stumping off a wide is
    // common; hit-wicket can occur off any delivery). Run Out is always allowed.
    const isNoball = pendingExtraType === "noball";
    const isWide = pendingExtraType === "wide";
    const blockedOnNoball =
      dismissalType === "Bowled" ||
      dismissalType === "Caught" ||
      dismissalType === "LBW" ||
      dismissalType === "Stumped" ||
      dismissalType === "Hit Wicket";
    const blockedOnWide =
      dismissalType === "Bowled" ||
      dismissalType === "Caught" ||
      dismissalType === "LBW";
    if ((isNoball && blockedOnNoball) || (isWide && blockedOnWide)) {
      showToast(`${dismissalType} cannot be given on a ${isNoball ? "no-ball" : "wide"}`, "warning");
      return;
    }

    playWicketSfx();
    playWicketSound();
    if (enableVisualEffects) {
      setScoringEffect("wicket");
    }

    const fielderPlayer = selectedFielderId ? bowlingTeam.find(p => p.id === selectedFielderId) : null;

    let dismissalText = "Wicket";

    if (dismissalType === "Bowled") {
      dismissalText = `b ${bowler?.name || "bowler"}`;
    } else if (dismissalType === "Caught") {
      if (fielderPlayer) {
        const isKeeper = fielderPlayer.id === bowlingTeamKeeperId;
        dismissalText = isKeeper
          ? `c †${fielderPlayer.name} b ${bowler?.name || "bowler"}`
          : `c ${fielderPlayer.name} b ${bowler?.name || "bowler"}`;
      } else {
        dismissalText = `c & b ${bowler?.name || "bowler"}`;
      }
    } else if (dismissalType === "LBW") {
      dismissalText = `lbw b ${bowler?.name || "bowler"}`;
    } else if (dismissalType === "Run Out") {
      dismissalText = fielderPlayer ? `run out (${fielderPlayer.name})` : "run out";
    } else if (dismissalType === "Stumped") {
      const keeper = bowlingTeamKeeper || fielderPlayer;
      dismissalText = keeper
        ? `st †${keeper.name} b ${bowler?.name || "bowler"}`
        : `st b ${bowler?.name || "bowler"}`;
    } else if (dismissalType === "Hit Wicket") {
      dismissalText = `hit wicket b ${bowler?.name || "bowler"}`;
    } else {
      dismissalText = dismissalType;
    }

    // For run-out, use the selected batter; otherwise use striker
    const dismissedBatterId = dismissalType === "Run Out" && runOutBatterId
      ? runOutBatterId
      : state.strikerId;

    if (inning.legalBalls % ballsPerOver === ballsPerOver - 1) {
      setLastOverSnapshot(JSON.parse(JSON.stringify(state)));
    ``} else if (inning.legalBalls % ballsPerOver === 0 && lastOverSnapshot) {
      setLastOverSnapshot(null);
    }

    const pendingExtra = pendingExtraType;
    const extraRuns = pendingRuns ?? 0;

    let delRuns = 0;
    let delIsLegal = true;
    let delExtraType: "wide" | "noball" | "bye" | "legbye" | null = null;
    let delDisplay = "W";

    if (pendingExtra === "noball") {
      delRuns = 1 + extraRuns;
      delIsLegal = false;
      delExtraType = "noball";
      delDisplay = extraRuns > 0 ? `Nb+${extraRuns} W` : "Nb W";
    } else if (pendingExtra === "wide") {
      delRuns = 1 + extraRuns;
      delIsLegal = false;
      delExtraType = "wide";
      delDisplay = extraRuns > 0 ? `Wd+${extraRuns} W` : "Wd W";
    }

    const del = {
      runs: delRuns,
      isLegal: delIsLegal,
      isWicket: true,
      batterId: dismissedBatterId || undefined,
      bowlerId: state.bowlerId || undefined,
      dismissal: dismissalText,
      dismissalType: dismissalType,
      fielderId: selectedFielderId || null,
      display: delDisplay,
      extraType: delExtraType,
      battersCrossed: (dismissalType === "Run Out" || dismissalType === "Caught") ? battersCrossed : undefined,
    };

    const newState = addDelivery(state, del);

    // For run-out and caught: handle striker rotation based on crossing.
    // Caught: if the batters crossed before the catch was completed, the
    // non-striker is now at the striker's end, so the incoming batter will
    // come in at the non-striker end (handled by the batsman selector flow).
    if ((dismissalType === "Run Out" || dismissalType === "Caught") && battersCrossed) {
      const temp = newState.strikerId;
      newState.strikerId = newState.nonStrikerId;
      newState.nonStrikerId = temp;
    }

    const lastCommentaryText = newState.commentary.length > 0
      ? newState.commentary[newState.commentary.length - 1].text
      : null;
    const delIdx = newState.innings[newState.currentInnings].deliveries.length - 1;
    const gen = generateDeliveryCommentary(
      newState,
      newState.innings[newState.currentInnings].deliveries[delIdx],
      delIdx,
      newState.currentInnings,
      lastCommentaryText,
    );
    newState.commentary.push({
      over: gen.over,
      ball: gen.ball,
      text: gen.text,
      auto: true,
      deliveryIndex: gen.deliveryIndex,
      inningsIndex: gen.inningsIndex,
    });

    soundManager.play('wicket');

    setState(newState);

    // Trigger feedback AFTER state is committed
    triggerScoringFeedback("WICKET!", "wicket");

    setShowDismissalModal(false);
    setDismissalType("");
    setSelectedFielderId("");
    setRunOutBatterId("");
    setBattersCrossed(false);
    clearPendingDelivery();

    // Track which crease slot was vacated so the incoming batter goes to the correct end
    const vacatedSlot = !newState.strikerId ? "striker" : "nonstriker";
    setIncomingBatterSlot(vacatedSlot);

    // Set waiting for new batsman (non-blocking UX)
    if (import.meta.env.DEV) {
      console.log('[LiveScore UX] Wicket recorded:', {
        dismissedBatterId,
        newStrikerId: newState.strikerId,
        nonStrikerId: newState.nonStrikerId,
        settingWaitingForNewBatsman: true
      });
    }
    setWaitingForNewBatsman(true);

    // Auto-open the player selector immediately so the user doesn't have to find and tap the button
    // This keeps selection in the top area (CurrentPlayersCard section) via InlinePlayerSelector modal
    setShowPlayerSelector("batsman");
    showToast("Wicket! Select new batsman", "info");
  }

  function handleMatchComplete() {
    if (state.status === 'completed' || state.status === 'Completed') {
      return;
    }
    const newState = { ...state, status: 'completed' };
    setState(newState);
    if (onMatchComplete) {
      onMatchComplete();
    }
  }

  const triggerScoringFeedback = React.useCallback((message: string, type: "run" | "extra" | "wicket" | "success") => {
    console.log("[FEEDBACK TRIGGERED]", type, message);

    // Direct vibration — no library dependency
    if (navigator.vibrate) {
      navigator.vibrate(type === "wicket" ? [55, 30, 55] : type === "extra" ? 40 : 35);
      console.log("[VIBRATION TRIGGERED]");
    }

    // Show popup
    setScoringConfirmation({ message, type, visible: true });
    if (confirmationTimerRef.current) clearTimeout(confirmationTimerRef.current);
    confirmationTimerRef.current = setTimeout(() => {
      setScoringConfirmation(null);
    }, 800);

    // Anti-double-tap: Disable scoring buttons for 350ms
    setScoringButtonsDisabled(true);
    setTimeout(() => {
      setScoringButtonsDisabled(false);
    }, 350);
  }, []);

  function score(params: {
    runs: number;
    legal: boolean;
    display: string;
    extraType?: "wide" | "noball" | "bye" | "legbye" | null;
    wallBonus?: boolean;
    runsForRotation?: number;
  }) {
    let feedbackMessage = "";
    let feedbackType: "run" | "extra" | "wicket" | "success" = "run";

    if (params.wallBonus) {
      feedbackMessage = "W+1";
      feedbackType = "success";
    } else if (params.extraType) {
      const extraShort = params.extraType === "noball" ? "NB" :
                         params.extraType === "legbye" ? "LB" :
                         params.extraType === "bye" ? "B" : "WD";
      feedbackMessage = params.runs > 0 ? `${extraShort} +${params.runs}` : extraShort;
      feedbackType = "extra";
    } else if (params.runs === 6) {
      feedbackMessage = "SIX!";
      feedbackType = "success";
    } else if (params.runs === 4) {
      feedbackMessage = "FOUR!";
      feedbackType = "success";
    } else if (params.runs === 0) {
      feedbackMessage = "DOT";
      feedbackType = "run";
    } else {
      feedbackMessage = `+${params.runs}`;
      feedbackType = "run";
    }

    if (params.runs === 6 && !params.extraType) {
      playSixSound();
      if (enableVisualEffects) {
        setScoringEffect("six");
      }
    }

    if (params.runs === 4 && !params.extraType) {
      playFourSound();
      if (enableVisualEffects) {
        setScoringEffect("four");
      }
    }

    if (params.legal && inning.legalBalls % ballsPerOver === ballsPerOver - 1) {
      setLastOverSnapshot(JSON.parse(JSON.stringify(state)));
    } else if (params.legal && inning.legalBalls % ballsPerOver === 0 && lastOverSnapshot) {
      setLastOverSnapshot(null);
    }

    const del = {
  runs: params.runs,
  isLegal: params.legal,
  isWicket: false,
  batterId: state.strikerId || undefined,
  bowlerId: state.bowlerId || undefined,
  dismissal: null,
  display: params.display,
  extraType: params.extraType || null,

  // keep boolean safe
  wallBonus: params.wallBonus ?? undefined,

  // ✅ OPTION 1 FIX: forward rotation info
  runsForRotation: params.runsForRotation ??
    (params.wallBonus ? 1 : params.runs),
};


    const newState = addDelivery(state, del);

    const lastCommentaryText = newState.commentary.length > 0
      ? newState.commentary[newState.commentary.length - 1].text
      : null;
    const delIdx = newState.innings[newState.currentInnings].deliveries.length - 1;
    const gen = generateDeliveryCommentary(
      newState,
      newState.innings[newState.currentInnings].deliveries[delIdx],
      delIdx,
      newState.currentInnings,
      lastCommentaryText,
    );
    newState.commentary.push({
      over: gen.over,
      ball: gen.ball,
      text: gen.text,
      auto: true,
      deliveryIndex: gen.deliveryIndex,
      inningsIndex: gen.inningsIndex,
    });

    if (params.runs === 6 && !params.extraType) {
      soundManager.play('six');
    } else if (params.runs === 4 && !params.extraType) {
      soundManager.play('four');
    }

    setState(newState);

    // Trigger feedback AFTER state is committed
    triggerScoringFeedback(feedbackMessage, feedbackType);
  }

  function clearPendingDelivery() {
    setPendingExtraType(null);
    setPendingRuns(null);
    setPendingWallBonus(false);
  }

  function confirmPendingDelivery() {
    const runs = pendingRuns !== null ? pendingRuns : 0;
    let totalRuns = runs;
    let isLegal = true;
    let display = String(runs);

    if (pendingExtraType === "noball") {
      totalRuns = 1 + runs;
      isLegal = false;
      display = runs > 0 ? `Nb+${runs}` : "Nb";
    } else if (pendingExtraType === "wide") {
      totalRuns = 1 + runs;
      isLegal = false;
      display = runs > 0 ? `Wd+${runs}` : "Wd";
    } else if (pendingExtraType === "bye") {
      isLegal = true;
      display = runs > 0 ? `B${runs}` : "B";
    } else if (pendingExtraType === "legbye") {
      isLegal = true;
      display = runs > 0 ? `Lb${runs}` : "Lb";
    } else if (pendingWallBonus) {
      totalRuns = runs + 2; // Wall bonus: player runs + wall adds 1 = 2 total runs
      isLegal = true;
      display = "W+1";
    } else if (runs === 0) {
      display = "0";
    }

    if (runs === 6 && !pendingExtraType) {
      playSixSfx();
    } else if (runs === 4 && !pendingExtraType) {
      playFourSfx();
    }

    score({
      runs: totalRuns,
      legal: isLegal,
      display,
      extraType: pendingExtraType,
      wallBonus: pendingWallBonus,
      runsForRotation: pendingWallBonus ? 1 : undefined,
    });

    clearPendingDelivery();
  }

  function addCommentary() {
    if (!commentaryText.trim()) return;

    const currentOvers = Math.floor(inning.legalBalls / ballsPerOver);
    const currentBalls = inning.legalBalls % ballsPerOver;

    setState({
      ...state,
      commentary: [
        ...state.commentary,
        {
          over: currentOvers,
          ball: currentBalls,
          text: commentaryText.trim(),
          auto: false,
        },
      ],
    });

    setCommentaryText("");
  }

  const ballsPerOver = getBallsPerOver(state);

  // Over display
  const overs = Math.floor(inning.totalBalls / ballsPerOver);
  const balls = inning.totalBalls % ballsPerOver;

  // Stats
  const crr = getCurrentRunRate(inning, ballsPerOver);
  const rrr = getRequiredRunRate(inning);
  const partnership = getPartnership(state, ballsPerOver);
  const projected = getProjectedScore(inning, ballsPerOver);

  const tossSet = state.toss?.winnerTeam && state.toss?.decision;
  const hasLegalBalls = inning.totalBalls > 0;

  function handleApplyToss() {
    if (!tossWinner || !tossDecision) return;
    const newState = { ...state };
    applyToss(newState, tossWinner, tossDecision);
    setState(newState);
    setTossWinner("");
    setTossDecision("");
  }

  const requiresFielder = dismissalType === "Caught" || dismissalType === "Run Out" || dismissalType === "Stumped";

  // Player selection handlers
  const handleSelectNewBatsman = (playerId: string) => {
    if (pendingPlayerChange?.role === "striker") {
      if (playerId === state.nonStrikerId) {
        showToast("Same player cannot be at both ends", "warning");
        return;
      }
      const newState = { ...state, strikerId: playerId, updatedAt: new Date().toISOString() };
      setShowPlayerSelector(null);
      setPendingPlayerChange(null);
      setState(newState);
      showToast("Striker replaced", "success");
      return;
    }

    const newState = setNextBatter(state, playerId, incomingBatterSlot || undefined);
    setWaitingForNewBatsman(false);
    setShowPlayerSelector(null);
    setPendingPlayerChange(null);
    setIncomingBatterSlot(null);
    setState(newState);
    showToast("Batsman selected – scoring enabled", "success");
  };

  const handleSelectNonStriker = (playerId: string) => {
    if (pendingPlayerChange?.role === "nonstriker") {
      if (playerId === state.strikerId) {
        showToast("Same player cannot be at both ends", "warning");
        return;
      }
      const newState = { ...JSON.parse(JSON.stringify(state)), nonStrikerId: playerId, updatedAt: new Date().toISOString() };
      setShowPlayerSelector(null);
      setPendingPlayerChange(null);
      setState(newState);
      showToast("Non-striker replaced", "success");
      return;
    }

    const newState = { ...JSON.parse(JSON.stringify(state)), nonStrikerId: playerId, updatedAt: new Date().toISOString() };
    setWaitingForNewBatsman(false);
    setShowPlayerSelector(null);
    setPendingPlayerChange(null);
    setState(newState);
    showToast("Non-striker selected", "success");
  };

  const handleSelectNewBowler = (playerId: string) => {
    if (pendingPlayerChange?.role === "bowler") {
      const newState = setNextBowler(state, playerId);
      setShowPlayerSelector(null);
      setPendingPlayerChange(null);
      setState(newState);
      showToast("Bowler replaced", "success");
      return;
    }

    const newState = setNextBowler(state, playerId);
    setWaitingForNewBowler(false);
    setShowPlayerSelector(null);
    setPendingPlayerChange(null);
    handledOverBoundaryRef.current = inning.legalBalls;
    setState(newState);
    showToast("Bowler selected – scoring enabled", "success");
  };

  // Guard: show toast if user tries to score while locked
  const handleScoringAttemptWhileLocked = () => {
    if (waitingForNewBatsman || !state.strikerId) {
      showToast("Select new batsman", "warning");
    } else if (waitingForNewBowler || !state.bowlerId) {
      showToast("Select new bowler", "warning");
    }
  };

  return (
    <div
      className="p-4 text-white relative livescore-broadcast"
      style={{ paddingBottom: "calc(96px + env(safe-area-inset-bottom, 0px))" }}
    >
      <Watermark />

      {/* Toss Setup Panel */}
      {!tossSet && (
        <div className="bg-blue-900 border border-blue-700 rounded-lg p-4 mb-4 lg:max-w-2xl lg:mx-auto">
          <h3 className="text-lg font-bold mb-3">Match Setup - Toss</h3>
          <div className="space-y-3">
            <div>
              <label className="text-sm block mb-1">Toss Winner:</label>
              <select
                value={tossWinner}
                onChange={(e) => setTossWinner(e.target.value as "A" | "B" | "")}
                className="bg-slate-700 px-3 py-2 rounded w-full"
              >
                <option value="">-- Select Team --</option>
                <option value="A">{state.teamAName}</option>
                <option value="B">{state.teamBName}</option>
              </select>
            </div>
            <div>
              <label className="text-sm block mb-1">Decision:</label>
              <select
                value={tossDecision}
                onChange={(e) => setTossDecision(e.target.value as "BAT" | "BOWL" | "")}
                className="bg-slate-700 px-3 py-2 rounded w-full"
              >
                <option value="">-- Select Decision --</option>
                <option value="BAT">Bat First</option>
                <option value="BOWL">Bowl First</option>
              </select>
            </div>
            <button
              onClick={handleApplyToss}
              disabled={!tossWinner || !tossDecision}
              className="bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed px-4 py-2 rounded font-semibold w-full"
            >
              Apply Toss
            </button>
          </div>
        </div>
      )}

      {/* Warning when toss not set */}
      {!tossSet && hasLegalBalls && (
        <div className="bg-yellow-900 border border-yellow-700 rounded-lg p-3 mb-4 text-yellow-200">
          Set toss winner and decision to start the match.
        </div>
      )}

      {/* Scoring guard warning */}
      {!canScore && !inning.completed && (
        <div className="bg-orange-900 border border-orange-700 rounded-lg p-3 mb-4 text-orange-200">
          {!state.strikerId && "Select striker to continue scoring."}
          {!state.nonStrikerId && state.strikerId && (
            <button
              onClick={() => {
                console.log('[DEBUG] Guard banner: tapped to select non-striker. strikerId:', state.strikerId);
                setShowPlayerSelector("nonstriker");
              }}
              className="underline font-semibold hover:text-orange-100 transition-colors"
            >
              Select non-striker to continue scoring. Tap here.
            </button>
          )}
          {!state.bowlerId && state.strikerId && state.nonStrikerId && "Select bowler to continue scoring."}
        </div>
      )}

      {/* First Innings Complete — Premium Modal */}
      {inning.completed && state.currentInnings === 0 && state.inningsCount === 2 && (
        <InningsCompleteModal
          state={state}
          firstInning={inning}
          onStartSecondInnings={() => {
            const newState = { ...state };
            startNextInnings(newState);
            setState(newState);
          }}
        />
      )}

      {/* Match Complete — Premium Modal (match already marked completed) */}
      {isMatchCompleted && inning.completed && (
        <MatchCompleteModal
          state={state}
          onViewSummary={() => { if (onViewSummary) onViewSummary(); }}
          onBackToMatches={() => { if (onBackToMatches) onBackToMatches(); }}
        />
      )}

      {/* 2nd Innings Complete — End Match action (match not yet marked completed) */}
      {inning.completed && state.currentInnings === 1 && !isMatchCompleted && state.inningsCount === 2 && (
        <div className="bg-blue-900 border border-blue-700 rounded-lg p-4 mb-4 lg:max-w-3xl lg:mx-auto">
          {(() => {
            const result = getMatchResult(state);
            if (result) {
              return (
                <div>
                  <div className="text-lg font-bold text-green-300 mb-3">{result}</div>
                  <button
                    onClick={handleMatchComplete}
                    className="bg-blue-600 hover:bg-blue-700 px-6 py-3 rounded-lg font-bold w-full"
                  >
                    End Match
                  </button>
                </div>
              );
            }
            return null;
          })()}
        </div>
      )}

      {/* Inline Banners */}
      {waitingForNewBatsman && (
        <div className="bg-gradient-to-r from-emerald-900/90 to-emerald-800/90 border-2 border-emerald-500/50 rounded-xl p-4 mb-4 animate-slide-down">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-emerald-500/30 flex items-center justify-center text-2xl animate-pulse">
              🏏
            </div>
            <div>
              <div className="text-white font-bold text-lg">New Batsman Required</div>
              <div className="text-emerald-200 text-sm">A wicket has fallen – select a new batsman to continue scoring</div>
            </div>
          </div>
        </div>
      )}

      {waitingForNewBowler && !inning.completed && (
        <div className="bg-gradient-to-r from-red-900/90 to-red-800/90 border-2 border-red-500/50 rounded-xl p-4 mb-4 animate-slide-down">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-red-500/30 flex items-center justify-center text-2xl animate-pulse">
              ⚾
            </div>
            <div>
              <div className="text-white font-bold text-lg">Over Complete – New Bowler Required</div>
              <div className="text-red-200 text-sm">Select a new bowler to start the next over</div>
            </div>
          </div>
        </div>
      )}

      {/* V219: Team Score Area — both teams with logos */}
      {(() => {
        const battingTeamA = inning.battingTeam === "A";
        const battingName = battingTeamA ? state.teamAName : state.teamBName;
        const battingLogo = battingTeamA ? state.teamALogo : state.teamBLogo;
        const bowlingName = battingTeamA ? state.teamBName : state.teamAName;
        const bowlingLogo = battingTeamA ? state.teamBLogo : state.teamALogo;
        const battingScore = `${inning.totalRuns}/${inning.wickets}`;
        const battingOvers = `${overs}.${balls}`;
        const firstInnings = state.innings[0];
        const secondInnings = state.innings[1];
        const isFirstInningsBatting = state.currentInnings === 0;
        const bowlingTeamFirstInnings = !isFirstInningsBatting ? firstInnings : null;
        const battingTeamFirstInnings = isFirstInningsBatting ? null : firstInnings;
        const bowlingScore = bowlingTeamFirstInnings
          ? `${bowlingTeamFirstInnings.totalRuns}/${bowlingTeamFirstInnings.wickets}`
          : null;
        const isChase = !isFirstInningsBatting && inning.target != null;
        const needRuns = isChase ? (inning.target! - inning.totalRuns) : 0;
        const ballsRemaining = isChase && state.oversLimit
          ? (state.oversLimit * ballsPerOver - inning.totalBalls)
          : 0;
        const matchResult = getMatchResult(state);

        return (
          <div className="v219-score-area">
            {/* Status pill */}
            <div className="v219-status-row">
              <div className="v219-status-pill">
                {isMatchCompleted ? (
                  <span className="v219-status-done">COMPLETE</span>
                ) : (
                  <>
                    <span className="v219-live-dot" />
                    <span className="v219-live-text">LIVE</span>
                    <span className="v219-innings-text">
                      {state.currentInnings === 0 ? "1st Innings" : state.currentInnings === 1 ? "2nd Innings" : `${state.currentInnings + 1}th Innings`}
                    </span>
                  </>
                )}
              </div>
            </div>

            {/* Team A vs Team B line */}
            <div className="v219-vs-line">
              {state.teamAName} <span className="v219-vs-word">vs</span> {state.teamBName}
            </div>

            {/* Both teams — vertical layout: logo above name above score */}
            <div className="v219-teams-grid">
              {/* Batting team */}
              <div className={`v219-team-card v219-team-batting ${isChase ? "v219-team-chase" : ""}`}>
                <div className="v219-team-logo-wrap">
                  <TeamLogo logoUrl={battingLogo} teamName={battingName} size="md" />
                </div>
                <div className="v219-team-name">{battingName}</div>
                {isMatchCompleted && matchResult ? (
                  <div className="v219-team-score">{battingScore}</div>
                ) : (
                  <div className="v219-team-score">{battingScore}</div>
                )}
                <div className="v219-team-overs">{battingOvers} overs</div>
              </div>

              {/* Bowling/other team */}
              <div className="v219-team-card v219-team-bowling">
                <div className="v219-team-logo-wrap">
                  <TeamLogo logoUrl={bowlingLogo} teamName={bowlingName} size="md" />
                </div>
                <div className="v219-team-name">{bowlingName}</div>
                {bowlingScore ? (
                  <>
                    <div className="v219-team-score v219-team-score-secondary">{bowlingScore}</div>
                    <div className="v219-team-overs">
                      {bowlingTeamFirstInnings
                        ? `${Math.floor(bowlingTeamFirstInnings.totalBalls / ballsPerOver)}.${bowlingTeamFirstInnings.totalBalls % ballsPerOver} overs`
                        : ""}
                    </div>
                  </>
                ) : (
                  <div className="v219-yet-to-bat">YET TO BAT</div>
                )}
              </div>
            </div>

            {/* Chase info — second innings in progress (including 0/0 before first delivery) */}
            {isChase && !isMatchCompleted && !inning.completed && (
              <div className="v219-chase-bar">
                <div className="v219-chase-main">
                  Need <b>{needRuns}</b> runs from <b>{ballsRemaining}</b> balls
                </div>
                <div className="v219-chase-rates">
                  <span>CRR <b>{crr.toFixed(2)}</b></span>
                  <span>RRR <b>{rrr === null ? "—" : rrr > 36 ? "36+" : rrr.toFixed(2)}</b></span>
                </div>
              </div>
            )}
            {/* Target during innings break — first innings done, second not yet started */}
            {isFirstInningsBatting && inning.completed && state.inningsCount === 2 && (
              <div className="v219-chase-bar">
                <div className="v219-chase-main">Target <b>{firstInnings.totalRuns + 1}</b></div>
              </div>
            )}
            {/* Match result */}
            {matchResult && (
              <div className="v219-chase-bar v219-result-bar">
                <div className="v219-chase-main">{matchResult}</div>
              </div>
            )}
          </div>
        );
      })()}

      {/* Main Layout: single compact column */}
      <div className="v219-main-grid">
        {/* Current Players + Scoring */}
        <div className="space-y-3">
          {/* This Over — compact full-width section above Current Players */}
          {tossSet && (() => {
            const overDeliveries = getCurrentOverDeliveries(inning, ballsPerOver);
            if (overDeliveries.length === 0) return null;
            return (
              <div className="v222-this-over">
                <span className="v222-over-label">This Over</span>
                <div className="v222-over-chips">
                  {overDeliveries.map((d, i) => (
                    <span key={i} className={deliveryChipClass(d)}>{d.display}</span>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* Current Players — Striker → Non-Striker → Bowler */}
          <CurrentPlayersCard
            striker={striker}
            nonStriker={nonStriker}
            bowler={bowler}
            inning={inning}
            ballsPerOver={ballsPerOver}
            tossSet={!!tossSet}
            onChangeStriker={() => {
              if (!tossSet) { showToast("Complete toss first", "warning"); return; }
              const prevId = state.strikerId || null;
              const prevName = striker ? formatPlayerName(striker.name) : "";
              setPrePlayerChangeSnapshot(JSON.parse(JSON.stringify(state)));
              setPendingPlayerChange({ role: "striker", previousPlayerId: prevId, previousPlayerName: prevName });
              setShowPlayerSelector("batsman");
            }}
            onChangeNonStriker={() => {
              if (!tossSet) { showToast("Complete toss first", "warning"); return; }
              const prevId = state.nonStrikerId || null;
              const prevName = nonStriker ? formatPlayerName(nonStriker.name) : "";
              setPrePlayerChangeSnapshot(JSON.parse(JSON.stringify(state)));
              setPendingPlayerChange({ role: "nonstriker", previousPlayerId: prevId, previousPlayerName: prevName });
              setShowPlayerSelector("nonstriker");
            }}
            onChangeBowler={() => {
              if (!tossSet) { showToast("Complete toss first", "warning"); return; }
              const prevId = state.bowlerId || null;
              const prevName = bowler ? formatPlayerName(bowler.name) : "";
              setPrePlayerChangeSnapshot(JSON.parse(JSON.stringify(state)));
              setPendingPlayerChange({ role: "bowler", previousPlayerId: prevId, previousPlayerName: prevName });
              setShowPlayerSelector("bowler");
            }}
            waitingForNewBatsman={waitingForNewBatsman}
            waitingForNewBowler={waitingForNewBowler}
            onSelectNewBatsman={() => {
              if (!tossSet) { showToast("Complete toss first", "warning"); return; }
              setPendingPlayerChange(null);
              setShowPlayerSelector("batsman");
            }}
            onSelectNewBowler={() => {
              if (!tossSet) { showToast("Complete toss first", "warning"); return; }
              setPendingPlayerChange(null);
              setShowPlayerSelector("bowler");
            }}
            onSelectNonStriker={() => {
              if (!tossSet) { showToast("Complete toss first", "warning"); return; }
              setPendingPlayerChange(null);
              setShowPlayerSelector("nonstriker");
            }}
            onRetireHurt={() => {
              if (!state.strikerId) return;
              const p = battingTeam.find(bp => bp.id === state.strikerId);
              setRetirePlayerId(state.strikerId);
              setRetirePlayerName(p ? formatPlayerName(p.name) : "");
              setShowRetireConfirm(true);
            }}
            onUndoPlayerChange={() => {
              if (prePlayerChangeSnapshot) {
                setState(prePlayerChangeSnapshot);
                setPrePlayerChangeSnapshot(null);
                setPendingPlayerChange(null);
                showToast("Player change undone", "info");
              }
            }}
            canUndoPlayerChange={!!prePlayerChangeSnapshot}
          />

          {/* Scoring Confirmation Popup */}
          {scoringConfirmation && (
            <div
              style={{
                position: "fixed",
                bottom: "140px",
                left: "50%",
                transform: "translateX(-50%)",
                zIndex: 9999,
                pointerEvents: "none",
                animation: "scorePopUp 0.8s ease-out forwards",
              }}
            >
              <div
                style={{
                  backgroundColor:
                    scoringConfirmation.type === "wicket" ? "rgba(220,38,38,0.95)" :
                    scoringConfirmation.type === "extra" ? "rgba(37,99,235,0.95)" :
                    scoringConfirmation.type === "success" ? "rgba(5,150,105,0.95)" :
                    "rgba(22,163,74,0.95)",
                  color: "#fff",
                  fontSize: "1.5rem",
                  fontWeight: 800,
                  padding: "10px 28px",
                  borderRadius: "999px",
                  boxShadow:
                    scoringConfirmation.type === "wicket" ? "0 0 20px rgba(220,38,38,0.6)" :
                    scoringConfirmation.type === "extra" ? "0 0 20px rgba(37,99,235,0.6)" :
                    scoringConfirmation.type === "success" ? "0 0 20px rgba(5,150,105,0.6)" :
                    "0 0 20px rgba(22,163,74,0.6)",
                  whiteSpace: "nowrap",
                  letterSpacing: "0.03em",
                }}
              >
                {scoringConfirmation.message}
              </div>
            </div>
          )}

          {/* Scoring Keypad */}
          <ScoringKeypad
            canScore={canScore && !scoringButtonsDisabled}
            tossSet={!!tossSet}
            isIndoor={isIndoor}
            pendingRuns={pendingRuns}
            pendingExtraType={pendingExtraType}
            pendingWallBonus={pendingWallBonus}
            onRunClick={(runs) => {
              if (scoringButtonsDisabled) return;
              // If an extra is selected, set pending runs for confirmation
              if (pendingExtraType !== null) {
                setPendingRuns(runs);
                setPendingWallBonus(false);
                return;
              }

              // Direct scoring for normal runs (no confirmation needed)
              if (inning.legalBalls % ballsPerOver === ballsPerOver - 1) {
                setLastOverSnapshot(JSON.parse(JSON.stringify(state)));
              } else if (inning.legalBalls % ballsPerOver === 0 && lastOverSnapshot) {
                setLastOverSnapshot(null);
              }

              if (runs === 6) {
                playSixSfx();
              } else if (runs === 4) {
                playFourSfx();
              }

              score({
                runs: runs,
                legal: true,
                display: runs === 0 ? '0' : String(runs),
                extraType: null,
                wallBonus: false
              });
            }}
            onWallBonusClick={() => {
              if (scoringButtonsDisabled) return;

              if (pendingExtraType !== null) {
                setPendingRuns(2);
                setPendingWallBonus(true);
                return;
              }

              // Direct scoring for W+1 (no confirmation needed)
              if (inning.legalBalls % ballsPerOver === ballsPerOver - 1) {
                setLastOverSnapshot(JSON.parse(JSON.stringify(state)));
              } else if (inning.legalBalls % ballsPerOver === 0 && lastOverSnapshot) {
                setLastOverSnapshot(null);
              }

              score({
                runs: 2,
                legal: true,
                display: 'W+1',
                extraType: null,
                wallBonus: true,
                runsForRotation: 1
              });
            }}
            onWicketClick={() => {
              if (scoringButtonsDisabled) return;
              setShowDismissalModal(true);
            }}
            onExtraClick={(type) => {
              if (scoringButtonsDisabled) return;
              setPendingExtraType(pendingExtraType === type ? null : type);
              setPendingWallBonus(false);
            }}
          />

          {/* Current Ball Display - Only for Extras */}
          {pendingExtraType !== null && (
            <div className="broadcast-glass-card rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold text-white flex items-center gap-2">
                  <span className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></span>
                  Extra Delivery
                </h3>
                <button
                  onClick={clearPendingDelivery}
                  className="text-slate-400 hover:text-white text-xl leading-none"
                >
                  ×
                </button>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <span className="text-slate-400">Extra Type:</span>
                  <span className="font-bold text-white capitalize">
                    {pendingExtraType === "noball" ? "No Ball" : pendingExtraType === "legbye" ? "Leg Bye" : pendingExtraType}
                  </span>
                </div>
                {pendingRuns !== null && (
                  <div className="flex items-center gap-2">
                    <span className="text-slate-400">Additional Runs:</span>
                    <span className="font-bold text-white">{pendingRuns}</span>
                  </div>
                )}
                <div className="pt-2 border-t border-slate-700/50">
                  <div className="flex items-center gap-2">
                    <span className="text-slate-400">Total Runs:</span>
                    <span className="font-bold text-emerald-400 text-lg">
                      {(pendingExtraType === "noball" || pendingExtraType === "wide")
                        ? (1 + (pendingRuns || 0))
                        : (pendingRuns !== null ? pendingRuns : 0)}
                    </span>
                  </div>
                </div>
              </div>
              <button
                onClick={confirmPendingDelivery}
                className="w-full bg-gradient-to-br from-emerald-600 to-emerald-700 hover:from-emerald-500 hover:to-emerald-600 px-6 py-3 rounded-xl font-bold mt-4 shadow-lg transition-all"
              >
                Confirm Extra
              </button>
            </div>
          )}

          {/* Action Buttons — Undo / Swap / End Innings */}
          <div className="grid grid-cols-3 gap-2">
            {showProFeatures ? (
              <button
                className="rounded-xl border border-white/10 bg-white/[0.04] hover:bg-white/[0.08] disabled:opacity-40 disabled:cursor-not-allowed px-3 py-3 flex flex-col items-center justify-center gap-1 transition-colors"
                onClick={() => {
                  if (inning.deliveries.length === 0) return;
                  const lastDelivery = inning.deliveries[inning.deliveries.length - 1];
                  const overJustCompleted = inning.legalBalls % ballsPerOver === 0 && lastDelivery.isLegal && lastOverSnapshot;
                  if (overJustCompleted) {
                    setShowUndoOverModal(true);
                  } else {
                    const newState = undoLastDelivery(state);
                    if (newState.commentary.length > 0) {
                      newState.commentary.pop();
                    }
                    setState(newState);
                    if (newState.strikerId) setWaitingForNewBatsman(false);
                    const restoredInning = newState.innings[newState.currentInnings];
                    if (!restoredInning || restoredInning.legalBalls % ballsPerOver !== 0 || restoredInning.legalBalls === 0) {
                      setWaitingForNewBowler(false);
                    }
                  }
                }}
                disabled={inning.deliveries.length === 0 || inning.completed}
                title="Undo works even when scoring is locked"
              >
                <svg className="w-5 h-5 text-white/80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h11a4 4 0 110 8h-1M3 10l5-5M3 10l5 5" />
                </svg>
                <div className="text-white text-sm font-bold leading-none">Undo</div>
                <div className="text-white/50 text-[10px] leading-none">Last Ball</div>
              </button>
            ) : (
              <button
                className="rounded-xl border border-white/10 bg-white/[0.04] opacity-50 cursor-not-allowed px-3 py-3 flex flex-col items-center justify-center gap-1"
                disabled
                title="Available in WatchWicket Pro"
              >
                <span className="text-white/70 text-lg">🔒</span>
                <div className="text-white text-sm font-bold leading-none">Undo</div>
                <div className="text-white/50 text-[10px] leading-none">Pro only</div>
              </button>
            )}

            <button
              className="rounded-xl border border-white/10 bg-white/[0.04] hover:bg-white/[0.08] disabled:opacity-40 disabled:cursor-not-allowed px-3 py-3 flex flex-col items-center justify-center gap-1 transition-colors"
              onClick={() => {
                if (!state.strikerId || !state.nonStrikerId) {
                  showToast("Both batsmen required to swap", "warning");
                  return;
                }
                setState({
                  ...state,
                  strikerId: state.nonStrikerId,
                  nonStrikerId: state.strikerId,
                });
                showToast("Batsmen swapped", "info");
              }}
              disabled={!tossSet || !state.strikerId || !state.nonStrikerId || inning.completed}
            >
              <svg className="w-5 h-5 text-white/80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4M16 17H4m0 0l4 4m-4-4l4-4" />
              </svg>
              <div className="text-white text-sm font-bold leading-none">Swap</div>
              <div className="text-white/50 text-[10px] leading-none">Batsmen</div>
            </button>

            <button
              className="rounded-xl border border-red-500/20 bg-red-900/20 hover:bg-red-900/30 disabled:opacity-40 disabled:cursor-not-allowed px-3 py-3 flex flex-col items-center justify-center gap-1 transition-colors"
              onClick={() => {
                const newState = { ...state };
                endInnings(newState);
                setState(newState);
              }}
              disabled={inning.totalBalls === 0 || inning.completed}
            >
              <svg className="w-5 h-5 text-red-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
              <div className="text-red-200 text-sm font-bold leading-none">End</div>
              <div className="text-red-300/70 text-[10px] leading-none">Innings</div>
            </button>
          </div>

          <button
            onClick={() => setShowBatterManager(true)}
            disabled={!tossSet || inning.completed}
            className="mt-2 w-full rounded-xl border border-amber-500/20 bg-amber-900/20 hover:bg-amber-900/30 disabled:opacity-40 disabled:cursor-not-allowed px-3 py-2.5 flex items-center justify-center gap-2 transition-colors"
          >
            <svg className="w-4 h-4 text-amber-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            <span className="text-amber-100 text-sm font-bold">Manage Batters &amp; Corrections</span>
          </button>
        </div>
      </div>

      {/* V219: Indoor rule reminder strip */}
      {(() => {
        const overNum = Math.floor(inning.totalBalls / ballsPerOver);
        const ballNum = inning.totalBalls % ballsPerOver;
        const oversLabel = state.oversLimit === null ? "Unlimited" : `${state.oversLimit} overs`;
        const formatLabel = isIndoor ? "−3 Wickets (Indoor)" : (state.format === "INDOOR" ? "Indoor" : state.format);
        return (
          <div className="v219-rule-strip">
            Over {overNum}.{ballNum} | {oversLabel} | {formatLabel}
          </div>
        );
      })()}


      {/* Dismissal Modal */}
      {showDismissalModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold mb-4">Wicket - How was the batter out?</h3>

            {!dismissalType ? (
              <div className="space-y-2">
                <button
                  className="w-full bg-red-700 hover:bg-red-800 p-3 rounded font-semibold"
                  onClick={() => setDismissalType("Bowled")}
                >
                  Bowled
                </button>
                <button
                  className="w-full bg-red-700 hover:bg-red-800 p-3 rounded font-semibold"
                  onClick={() => setDismissalType("Caught")}
                >
                  Caught
                </button>
                <button
                  className="w-full bg-red-700 hover:bg-red-800 disabled:bg-gray-700 disabled:text-gray-500 disabled:cursor-not-allowed p-3 rounded font-semibold"
                  disabled={pendingExtraType === "noball"}
                  title={pendingExtraType === "noball" ? "LBW cannot be given on a no-ball" : undefined}
                  onClick={() => setDismissalType("LBW")}
                >
                  LBW{pendingExtraType === "noball" ? " (not allowed on a no-ball)" : ""}
                </button>
                <button
                  className="w-full bg-orange-700 hover:bg-orange-800 p-3 rounded font-semibold"
                  onClick={() => setDismissalType("Run Out")}
                >
                  Run Out
                </button>
                <button
                  className="w-full bg-purple-700 hover:bg-purple-800 p-3 rounded font-semibold"
                  onClick={() => {
                    setDismissalType("Stumped");
                    if (bowlingTeamKeeperId) {
                      setSelectedFielderId(bowlingTeamKeeperId);
                    }
                  }}
                >
                  Stumped
                </button>
                <button
                  className="w-full bg-red-700 hover:bg-red-800 p-3 rounded font-semibold"
                  onClick={() => setDismissalType("Hit Wicket")}
                >
                  Hit Wicket
                </button>
                <button
                  className="w-full bg-gray-700 hover:bg-gray-600 p-3 rounded font-semibold"
                  onClick={() => setDismissalType("Other")}
                >
                  Other
                </button>
                <button
                  className="w-full bg-gray-700 hover:bg-gray-600 p-3 rounded mt-2"
                  onClick={() => {
                    setShowDismissalModal(false);
                    setDismissalType("");
                    setSelectedFielderId("");
                  }}
                >
                  Cancel
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-gray-700 p-3 rounded">
                  <div className="text-sm text-gray-400">Dismissal Type:</div>
                  <div className="text-lg font-semibold">{dismissalType}</div>
                </div>

                {dismissalType === "Run Out" && (
                  <>
                    <div>
                      <label className="text-sm block mb-2">Which batter is out?</label>
                      <select
                        value={runOutBatterId}
                        onChange={(e) => setRunOutBatterId(e.target.value)}
                        className="bg-gray-700 p-2 rounded w-full"
                      >
                        <option value="">-- Select Batter --</option>
                        <option value={state.strikerId || ""}>
                          Striker: {striker ? getPlayerDisplayName(striker) : "Not selected"}
                        </option>
                        <option value={state.nonStrikerId || ""}>
                          Non-Striker: {nonStriker ? getPlayerDisplayName(nonStriker) : "Not selected"}
                        </option>
                      </select>
                    </div>
                    <div>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={battersCrossed}
                          onChange={(e) => setBattersCrossed(e.target.checked)}
                          className="w-5 h-5"
                        />
                        <span className="text-sm">Batters crossed</span>
                      </label>
                    </div>
                    <div>
                      <label className="text-sm block mb-2">Fielder (optional):</label>
                      <select
                        value={selectedFielderId}
                        onChange={(e) => setSelectedFielderId(e.target.value)}
                        className="bg-gray-700 p-2 rounded w-full"
                      >
                        <option value="">-- Select Fielder --</option>
                        {bowlingTeam.map((p) => (
                          <option value={p.id} key={p.id}>
                            {getPlayerDisplayName(p)}
                          </option>
                        ))}
                      </select>
                    </div>
                  </>
                )}

                {requiresFielder && dismissalType !== "Run Out" && (
                  <div>
                    <label className="text-sm block mb-2">
                      {dismissalType === "Stumped" ? "Wicket Keeper:" : "Fielder (who caught/fielded):"}
                    </label>
                    <select
                      value={selectedFielderId}
                      onChange={(e) => setSelectedFielderId(e.target.value)}
                      className="bg-gray-700 p-2 rounded w-full"
                    >
                      <option value="">-- Select {dismissalType === "Stumped" ? "Keeper" : "Fielder"} --</option>
                      {bowlingTeam.map((p) => (
                        <option value={p.id} key={p.id}>
                          {getPlayerDisplayName(p)}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {dismissalType === "Caught" && (
                  <div className="bg-gray-900/40 p-3 rounded">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={battersCrossed}
                        onChange={(e) => setBattersCrossed(e.target.checked)}
                        className="w-5 h-5"
                      />
                      <span className="text-sm">Did the batters cross before the catch?</span>
                    </label>
                    <p className="text-xs text-gray-400 mt-1 ml-7">
                      If yes, the non-striker takes strike; new batter comes in at the non-striker end.
                    </p>
                  </div>
                )}

                <div className="flex gap-2">
                  <button
                    className="flex-1 bg-green-700 hover:bg-green-800 disabled:bg-green-900 disabled:cursor-not-allowed p-3 rounded font-semibold"
                    disabled={
                      (dismissalType === "Caught" && !selectedFielderId) ||
                      (dismissalType === "Stumped" && !selectedFielderId) ||
                      (dismissalType === "Run Out" && !runOutBatterId)
                    }
                    onClick={handleDismissal}
                  >
                    Save
                  </button>
                  <button
                    className="flex-1 bg-gray-700 hover:bg-gray-600 p-3 rounded"
                    onClick={() => {
                      setDismissalType("");
                      setSelectedFielderId("");
                      setRunOutBatterId("");
                      setBattersCrossed(false);
                    }}
                  >
                    Back
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Scoring Effect Overlay */}
      {scoringEffect && (
        <EnhancedScoringEffects
          type={scoringEffect}
          onComplete={() => setScoringEffect(null)}
          batterHandedness="RHB"
        />
      )}

      {/* Undo Over Confirmation Modal */}
      <UndoOverModal
        show={showUndoOverModal}
        onConfirm={() => {
          if (lastOverSnapshot) {
            const restoredInning = lastOverSnapshot.innings[lastOverSnapshot.currentInnings];
            handledOverBoundaryRef.current = restoredInning ? restoredInning.legalBalls : null;
            setState(lastOverSnapshot);
            setLastOverSnapshot(null);
          }
          setWaitingForNewBowler(false);
          setShowPlayerSelector(null);
          setPendingPlayerChange(null);
          setShowUndoOverModal(false);
        }}
        onCancel={() => {
          setShowUndoOverModal(false);
        }}
      />

      {/* Inline Player Selector (Non-blocking) */}
      {showPlayerSelector && (() => {
        const isBowlerSelector = showPlayerSelector === "bowler";
        const isNonStrikerSelector = showPlayerSelector === "nonstriker";
        const playerPool = isBowlerSelector ? bowlingTeam : battingTeam;

        const selectorType = isNonStrikerSelector ? "nonstriker" : (isBowlerSelector ? "bowler" : "batsman");
        const reverseBattingActive = isIndoor && !!inning.reverseBattingActive;
        const dismissalHistory = isIndoor ? (inning.dismissalHistory || []) : [];

        const isReplacement = !!pendingPlayerChange;

        const lastDelivery = inning.deliveries.length > 0 ? inning.deliveries[inning.deliveries.length - 1] : null;
        const lastBowlerId = lastDelivery?.bowlerId ?? null;

        return (
          <InlinePlayerSelector
            type={selectorType}
            players={playerPool}
            onSelect={
              isBowlerSelector
                ? handleSelectNewBowler
                : isNonStrikerSelector
                  ? handleSelectNonStriker
                  : handleSelectNewBatsman
            }
            onClose={() => {
              setShowPlayerSelector(null);
              setPendingPlayerChange(null);
            }}
            currentStrikerId={state.strikerId || undefined}
            currentNonStrikerId={state.nonStrikerId || undefined}
            currentBowlerId={state.bowlerId || undefined}
            isIndoor={isIndoor}
            reverseBattingActive={reverseBattingActive}
            dismissalHistory={dismissalHistory}
            replacementMode={isReplacement}
            previousPlayerName={pendingPlayerChange?.previousPlayerName}
            bowlerStatsMap={bowlerStatsMap}
            lastBowlerId={lastBowlerId}
            isNextOverSelection={isBowlerSelector && waitingForNewBowler}
          />
        );
      })()}

      {/* Retire Hurt Confirmation */}
      {showRetireConfirm && retirePlayerId && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center p-4"
          onClick={() => {
            setShowRetireConfirm(false);
            setRetirePlayerId(null);
            setRetirePlayerName("");
          }}
        >
          <div className="absolute inset-0 bg-black/60" />
          <div
            className="relative bg-slate-900 border border-slate-700 rounded-2xl p-6 w-full max-w-sm shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-bold text-white mb-1">Confirm Retirement</h3>
            <p className="text-sm text-slate-300 mb-4">
              Retire <span className="font-bold text-orange-400">{retirePlayerName}</span> as Retired Hurt?
            </p>
            <div className="bg-slate-800/60 rounded-lg p-3 mb-4 text-xs text-slate-400 space-y-1">
              <p>The batter will keep all runs, balls, 4s and 6s already scored.</p>
              <p>They will NOT be marked as out or dismissed.</p>
              <p>They can return to bat later via "Return to Crease".</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowRetireConfirm(false);
                  setRetirePlayerId(null);
                  setRetirePlayerName("");
                }}
                className="flex-1 px-4 py-3 rounded-xl bg-slate-700 hover:bg-slate-600 text-white font-bold text-sm transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (retirePlayerId) {
                    handleRetireHurt(retirePlayerId);
                  }
                  setShowRetireConfirm(false);
                  setRetirePlayerId(null);
                  setRetirePlayerName("");
                  setShowBatterManager(false);
                }}
                className="flex-1 px-4 py-3 rounded-xl bg-orange-600 hover:bg-orange-500 text-white font-bold text-sm transition-colors"
              >
                Confirm Retirement
              </button>
            </div>
          </div>
        </div>
      )}

      {showBatterManager && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center sm:justify-center">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowBatterManager(false)} />
          <div className="relative bg-gradient-to-b from-slate-800 to-slate-900 w-full sm:max-w-lg sm:rounded-2xl rounded-t-3xl max-h-[80vh] flex flex-col shadow-2xl border border-white/10">
            <div className="flex items-center justify-between p-5 border-b border-white/10 shrink-0">
              <div>
                <h3 className="text-xl font-extrabold text-white">Manage Batters</h3>
                <p className="text-sm text-slate-400 mt-1">Retire hurt or correct a scoring mistake</p>
              </div>
              <button onClick={() => setShowBatterManager(false)} className="w-10 h-10 flex items-center justify-center rounded-full bg-slate-700/50 hover:bg-slate-700 text-white">×</button>
            </div>
            <div className="overflow-y-auto flex-1 p-4 space-y-2">
              {battingTeam.map((bp) => {
                const st = getBatterStatus(state, bp.id);
                const badge =
                  st === "out" ? { t: "Out", c: "bg-red-600/30 text-red-300" }
                  : st === "retired" ? { t: "Retired Hurt", c: "bg-orange-600/30 text-orange-300" }
                  : st === "batting" ? { t: "Batting", c: "bg-emerald-600/30 text-emerald-300" }
                  : { t: "Yet to Bat", c: "bg-slate-600/40 text-slate-300" };
                const slotOpen = !state.strikerId || !state.nonStrikerId;
                return (
                  <div key={bp.id} className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-3">
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-white font-bold truncate">{formatPlayerName(bp.name)}</div>
                        <div className="text-xs text-slate-400 mt-0.5">{bp.runs} ({bp.balls})</div>
                      </div>
                      <span className={`text-[11px] px-2 py-1 rounded font-bold whitespace-nowrap ${badge.c}`}>{badge.t}</span>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-3">
                      {st === "batting" && (
                        <button onClick={() => {
                          setRetirePlayerId(bp.id);
                          setRetirePlayerName(formatPlayerName(bp.name));
                          setShowRetireConfirm(true);
                        }}
                          className="text-xs font-semibold px-3 py-2 rounded-lg bg-orange-600/80 hover:bg-orange-600 text-white">Retire Hurt</button>
                      )}
                      {st === "retired" && slotOpen && (
                        <button onClick={() => { handleReturnRetired(bp.id); setShowBatterManager(false); }}
                          className="text-xs font-semibold px-3 py-2 rounded-lg bg-emerald-600/80 hover:bg-emerald-600 text-white">Return to Crease</button>
                      )}
                      {(st === "out" || st === "retired") && (
                        <button onClick={() => { handleAdminRestore(bp.id); setShowBatterManager(false); }}
                          className="text-xs font-semibold px-3 py-2 rounded-lg bg-blue-600/80 hover:bg-blue-600 text-white">{st === "out" ? "Undo Out (Restore)" : "Reverse Retirement"}</button>
                      )}
                      {st === "batting" && (
                        <button onClick={() => { handleAdminRestore(bp.id); setShowBatterManager(false); }}
                          className="text-xs font-semibold px-3 py-2 rounded-lg bg-slate-600/80 hover:bg-slate-600 text-white">Remove from Crease</button>
                      )}
                      {st === "yetToBat" && (
                        <span className="text-xs text-slate-500 py-2">No action needed</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <style>{`
        /* Broadcast theme styling for LiveScore */
        .livescore-broadcast .bg-blue-900 {
          background: rgba(20, 40, 60, 0.4) !important;
          border: 1px solid rgba(59, 130, 246, 0.2) !important;
          backdrop-filter: blur(16px);
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
        }

        .livescore-broadcast .bg-yellow-900 {
          background: rgba(60, 50, 20, 0.4) !important;
          border: 1px solid rgba(234, 179, 8, 0.2) !important;
          backdrop-filter: blur(16px);
        }

        .livescore-broadcast .bg-orange-900 {
          background: rgba(60, 40, 20, 0.4) !important;
          border: 1px solid rgba(249, 115, 22, 0.2) !important;
          backdrop-filter: blur(16px);
        }

        .livescore-broadcast .bg-gray-800 {
          background: rgba(15, 25, 20, 0.4) !important;
          border: 1px solid rgba(255, 255, 255, 0.08);
          backdrop-filter: blur(16px);
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
        }

        .livescore-broadcast .bg-gray-900 {
          background: rgba(10, 20, 15, 0.5) !important;
          border: 1px solid rgba(255, 255, 255, 0.06);
          backdrop-filter: blur(12px);
        }

        .livescore-broadcast .bg-slate-700 {
          background: rgba(51, 65, 85, 0.6) !important;
          border: 1px solid rgba(148, 163, 184, 0.1);
        }

        .livescore-broadcast .bg-gray-700 {
          background: rgba(55, 65, 81, 0.6) !important;
          border: 1px solid rgba(156, 163, 175, 0.1);
        }

        /* Enhanced scoring buttons */
        .livescore-broadcast button[class*="bg-green-7"] {
          background: linear-gradient(135deg, rgba(22, 163, 74, 0.9), rgba(21, 128, 61, 0.9)) !important;
          border: 1px solid rgba(34, 197, 94, 0.3);
          box-shadow: 0 4px 12px rgba(22, 163, 74, 0.3);
          transition: all 0.15s ease;
          font-weight: 700;
          letter-spacing: 0.02em;
        }

        .livescore-broadcast button[class*="bg-green-7"]:hover:not(:disabled) {
          background: linear-gradient(135deg, rgba(22, 163, 74, 1), rgba(21, 128, 61, 1)) !important;
          transform: translateY(-1px);
          box-shadow: 0 6px 16px rgba(22, 163, 74, 0.4);
        }

        .livescore-broadcast button[class*="bg-green-7"]:active:not(:disabled) {
          transform: translateY(0);
          box-shadow: 0 2px 8px rgba(22, 163, 74, 0.4);
        }

        .livescore-broadcast button[class*="bg-gray-6"] {
          background: rgba(75, 85, 99, 0.8) !important;
          border: 1px solid rgba(156, 163, 175, 0.2);
          font-weight: 700;
        }

        .livescore-broadcast button[class*="bg-gray-6"]:hover:not(:disabled) {
          background: rgba(75, 85, 99, 1) !important;
          border-color: rgba(156, 163, 175, 0.3);
        }

        .livescore-broadcast button[class*="bg-red-7"] {
          background: linear-gradient(135deg, rgba(185, 28, 28, 0.9), rgba(153, 27, 27, 0.9)) !important;
          border: 1px solid rgba(239, 68, 68, 0.3);
          box-shadow: 0 4px 12px rgba(185, 28, 28, 0.3);
          font-weight: 700;
        }

        .livescore-broadcast button[class*="bg-red-7"]:hover:not(:disabled) {
          background: linear-gradient(135deg, rgba(185, 28, 28, 1), rgba(153, 27, 27, 1)) !important;
          box-shadow: 0 6px 16px rgba(185, 28, 28, 0.4);
          transform: translateY(-1px);
        }

        .livescore-broadcast button[class*="bg-blue-7"] {
          background: linear-gradient(135deg, rgba(29, 78, 216, 0.9), rgba(30, 64, 175, 0.9)) !important;
          border: 1px solid rgba(59, 130, 246, 0.3);
          box-shadow: 0 4px 12px rgba(29, 78, 216, 0.3);
          font-weight: 600;
        }

        .livescore-broadcast button[class*="bg-blue-7"]:hover:not(:disabled) {
          background: linear-gradient(135deg, rgba(29, 78, 216, 1), rgba(30, 64, 175, 1)) !important;
          box-shadow: 0 6px 16px rgba(29, 78, 216, 0.4);
        }

        .livescore-broadcast button[class*="bg-yellow-6"] {
          background: linear-gradient(135deg, rgba(202, 138, 4, 0.9), rgba(161, 98, 7, 0.9)) !important;
          border: 1px solid rgba(234, 179, 8, 0.3);
          box-shadow: 0 4px 12px rgba(202, 138, 4, 0.3);
          font-weight: 700;
        }

        .livescore-broadcast button[class*="bg-yellow-6"]:hover:not(:disabled) {
          background: linear-gradient(135deg, rgba(202, 138, 4, 1), rgba(161, 98, 7, 1)) !important;
          box-shadow: 0 6px 16px rgba(202, 138, 4, 0.4);
        }

        .livescore-broadcast button[class*="bg-orange-6"] {
          background: linear-gradient(135deg, rgba(234, 88, 12, 0.9), rgba(194, 65, 12, 0.9)) !important;
          border: 1px solid rgba(249, 115, 22, 0.3);
          box-shadow: 0 4px 12px rgba(234, 88, 12, 0.3);
          font-weight: 600;
        }

        .livescore-broadcast button[class*="bg-orange-6"]:hover:not(:disabled) {
          background: linear-gradient(135deg, rgba(234, 88, 12, 1), rgba(194, 65, 12, 1)) !important;
          box-shadow: 0 6px 16px rgba(234, 88, 12, 0.4);
        }

        .livescore-broadcast button[class*="bg-purple-7"] {
          background: linear-gradient(135deg, rgba(126, 34, 206, 0.9), rgba(107, 33, 168, 0.9)) !important;
          border: 1px solid rgba(168, 85, 247, 0.3);
          box-shadow: 0 4px 12px rgba(126, 34, 206, 0.3);
          font-weight: 600;
        }

        .livescore-broadcast button[class*="bg-purple-7"]:hover:not(:disabled) {
          background: linear-gradient(135deg, rgba(126, 34, 206, 1), rgba(107, 33, 168, 1)) !important;
          box-shadow: 0 6px 16px rgba(126, 34, 206, 0.4);
        }

        /* Scoring button grid - larger tap targets */
        .livescore-broadcast .grid.grid-cols-4 button,
        .livescore-broadcast .grid.grid-cols-3 button {
          min-height: 3.5rem;
          font-size: 1.25rem;
          border-radius: 0.75rem;
        }

        /* Current ball panel */
        .livescore-broadcast .bg-gradient-to-r.from-blue-800 {
          background: linear-gradient(135deg, rgba(30, 58, 138, 0.6), rgba(29, 78, 216, 0.6)) !important;
          backdrop-filter: blur(20px);
          border: 1px solid rgba(59, 130, 246, 0.3) !important;
          box-shadow: 0 8px 24px rgba(29, 78, 216, 0.2);
        }

        /* Inputs */
        .livescore-broadcast input,
        .livescore-broadcast select {
          background: rgba(51, 65, 85, 0.7) !important;
          border: 1px solid rgba(148, 163, 184, 0.2);
          backdrop-filter: blur(8px);
        }

        .livescore-broadcast input:focus,
        .livescore-broadcast select:focus {
          border-color: rgba(16, 185, 129, 0.5);
          outline: none;
          box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
        }

        /* Modal backgrounds */
        .livescore-broadcast .fixed .bg-gray-800 {
          background: rgba(20, 30, 25, 0.98) !important;
          backdrop-filter: blur(24px);
          border: 1px solid rgba(255, 255, 255, 0.1);
        }

        /* Ensure text readability */
        .livescore-broadcast h2,
        .livescore-broadcast h3,
        .livescore-broadcast .text-white {
          text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
        }

        /* Active/selected state for scoring buttons */
        .livescore-broadcast button.ring-2.ring-blue-400 {
          border-color: rgba(96, 165, 250, 0.6) !important;
          box-shadow: 0 0 0 3px rgba(96, 165, 250, 0.2), 0 4px 12px rgba(59, 130, 246, 0.4) !important;
        }

        /* Disabled button styling */
        .livescore-broadcast button:disabled {
          opacity: 0.4;
          cursor: not-allowed;
          filter: grayscale(0.3);
        }

        /* Improve commentary section */
        .livescore-broadcast .space-y-2 > div {
          border-left: 2px solid rgba(16, 185, 129, 0.3);
          transition: all 0.2s ease;
        }

        .livescore-broadcast .space-y-2 > div:hover {
          border-left-color: rgba(16, 185, 129, 0.6);
          background: rgba(15, 25, 20, 0.6) !important;
        }

        /* Broadcast Layout Grid */
        .broadcast-main-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 1rem;
          margin-top: 1rem;
        }

        @media (min-width: 1024px) {
          .broadcast-main-grid {
            grid-template-columns: 2fr 3fr;
            gap: 1.5rem;
          }
        }

        .broadcast-glass-card {
          background: rgba(15, 25, 20, 0.4);
          border: 1px solid rgba(255, 255, 255, 0.08);
          backdrop-filter: blur(16px);
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
        }

        /* Mobile optimization - controls at top */
        @media (max-width: 1023px) {
          .broadcast-main-grid {
            display: flex;
            flex-direction: column-reverse;
          }
        }

        /* Inline banner animations */
        @keyframes slide-down {
          from {
            transform: translateY(-20px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        .animate-slide-down {
          animation: slide-down 0.4s ease-out;
        }

        /* Dominant scoring confirmation banner */
        @keyframes confirm-pop {
          0%   { transform: scale(0.82) translateY(6px); opacity: 0; }
          40%  { transform: scale(1.06) translateY(-2px); opacity: 1; }
          65%  { transform: scale(0.97) translateY(0); opacity: 1; }
          100% { transform: scale(1) translateY(0); opacity: 1; }
        }

        @keyframes confirm-fade-out {
          from { opacity: 1; transform: scale(1) translateY(0); }
          to   { opacity: 0; transform: scale(0.92) translateY(-4px); }
        }

        .scoring-confirm-banner {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.6rem;
          width: 100%;
          padding: 0.85rem 1.25rem;
          border-radius: 0.875rem;
          font-weight: 900;
          font-size: 1.5rem;
          letter-spacing: 0.04em;
          border: 2px solid rgba(255,255,255,0.18);
          box-shadow: 0 8px 32px rgba(0,0,0,0.45), 0 0 0 1px rgba(255,255,255,0.08);
          user-select: none;
        }

        .scoring-confirm-banner.scoring-confirm-in {
          animation: confirm-pop 0.22s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
        }

        .scoring-confirm-banner.scoring-confirm-out {
          animation: confirm-fade-out 0.22s ease forwards;
        }

        .scoring-confirm-text {
          color: white;
          text-shadow: 0 2px 8px rgba(0,0,0,0.5);
        }

        .scoring-confirm-check {
          color: rgba(255,255,255,0.85);
          font-size: 1.25rem;
          font-weight: 900;
          line-height: 1;
        }

        .scoring-confirm-run {
          background: linear-gradient(135deg, rgba(22, 101, 52, 0.95), rgba(15, 118, 75, 0.95));
          border-color: rgba(52, 211, 153, 0.4);
          box-shadow: 0 8px 32px rgba(0,0,0,0.45), 0 0 20px rgba(16, 185, 129, 0.25);
        }

        .scoring-confirm-extra {
          background: linear-gradient(135deg, rgba(29, 78, 216, 0.95), rgba(37, 99, 235, 0.95));
          border-color: rgba(147, 197, 253, 0.4);
          box-shadow: 0 8px 32px rgba(0,0,0,0.45), 0 0 20px rgba(96, 165, 250, 0.25);
        }

        .scoring-confirm-wicket {
          background: linear-gradient(135deg, rgba(185, 28, 28, 0.97), rgba(220, 38, 38, 0.97));
          border-color: rgba(252, 165, 165, 0.4);
          box-shadow: 0 8px 32px rgba(0,0,0,0.5), 0 0 28px rgba(239, 68, 68, 0.45);
          font-size: 1.75rem;
        }

        .scoring-confirm-success {
          background: linear-gradient(135deg, rgba(6, 95, 70, 0.97), rgba(4, 120, 87, 0.97));
          border-color: rgba(110, 231, 183, 0.45);
          box-shadow: 0 8px 32px rgba(0,0,0,0.45), 0 0 24px rgba(52, 211, 153, 0.35);
          font-size: 1.65rem;
        }

        /* V219 Premium LIVE redesign */
        .v219-score-area {
          background: linear-gradient(180deg, rgba(8,28,20,0.85) 0%, rgba(4,16,12,0.9) 100%);
          border: 1px solid rgba(16,185,129,0.15);
          border-radius: 1rem;
          padding: 0.875rem 0.625rem 1rem;
          margin-bottom: 0.75rem;
          box-shadow: 0 4px 20px rgba(0,0,0,0.3);
        }
        @media (min-width: 480px) {
          .v219-score-area { padding: 0.875rem 1rem 1rem; }
        }
        .v219-status-row { display: flex; align-items: center; justify-content: center; margin-bottom: 0.5rem; }
        .v219-status-pill {
          display: inline-flex;
          align-items: center;
          gap: 0.375rem;
          padding: 0.25rem 0.75rem;
          border-radius: 999px;
          background: rgba(220,38,38,0.15);
          border: 1px solid rgba(220,38,38,0.3);
        }
        .v219-live-dot {
          width: 6px; height: 6px;
          background: #ef4444;
          border-radius: 999px;
          animation: v219-pulse 1.5s ease-in-out infinite;
        }
        @keyframes v219-pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.4; } }
        .v219-live-text { color: #ef4444; font-weight: 800; font-size: 0.625rem; text-transform: uppercase; letter-spacing: 0.1em; }
        .v219-innings-text { color: #94a3b8; font-size: 0.625rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.06em; }
        .v219-status-done { color: #10b981; font-weight: 800; font-size: 0.625rem; text-transform: uppercase; letter-spacing: 0.1em; }
        .v219-vs-line {
          text-align: center;
          font-size: 0.8125rem;
          font-weight: 600;
          color: #e2e8f0;
          margin-bottom: 0.125rem;
        }
        .v219-vs-word { color: #64748b; font-weight: 400; margin: 0 0.25rem; }
        .v219-format-venue {
          text-align: center;
          font-size: 0.6875rem;
          color: #64748b;
          margin-bottom: 0.75rem;
        }
        .v219-dot-sep { margin: 0 0.375rem; }
        .v219-teams-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 0.5rem;
          min-width: 0;
        }
        .v219-team-card {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          gap: 0.25rem;
          padding: 0.625rem 0.5rem;
          border-radius: 0.75rem;
          border: 1px solid rgba(255,255,255,0.06);
          min-width: 0;
          overflow: hidden;
        }
        .v219-team-batting {
          background: linear-gradient(135deg, rgba(16,185,129,0.12), rgba(5,150,105,0.08));
          border-color: rgba(16,185,129,0.25);
        }
        .v219-team-chase {
          background: linear-gradient(135deg, rgba(16,185,129,0.18), rgba(5,150,105,0.12));
          border-color: rgba(16,185,129,0.35);
        }
        .v219-team-bowling {
          background: rgba(255,255,255,0.03);
        }
        .v219-team-logo-wrap {
          display: flex;
          justify-content: center;
          margin-bottom: 0.25rem;
        }
        .v219-team-name {
          font-size: 0.75rem;
          font-weight: 700;
          color: #e2e8f0;
          word-wrap: break-word;
          overflow-wrap: break-word;
          line-height: 1.2;
          max-width: 100%;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .v219-team-score {
          font-size: 1.25rem;
          font-weight: 900;
          color: #fff;
          line-height: 1.1;
        }
        @media (min-width: 480px) {
          .v219-team-score { font-size: 1.5rem; }
        }
        .v219-team-score-secondary {
          font-size: 1rem;
          color: #94a3b8;
        }
        @media (min-width: 480px) {
          .v219-team-score-secondary { font-size: 1.125rem; }
        }
        .v219-team-overs {
          font-size: 0.6875rem;
          color: #64748b;
        }
        .v219-yet-to-bat {
          font-size: 0.6875rem;
          font-weight: 700;
          color: #64748b;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          padding-top: 0.25rem;
        }
        .v219-chase-bar {
          margin-top: 0.625rem;
          padding: 0.5rem 0.75rem;
          border-radius: 0.625rem;
          background: rgba(16,185,129,0.1);
          border: 1px solid rgba(16,185,129,0.2);
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 0.375rem;
        }
        .v219-chase-main { font-size: 0.8125rem; color: #e2e8f0; font-weight: 600; }
        .v219-chase-main b { color: #10b981; font-weight: 800; }
        .v219-chase-rates { display: flex; gap: 0.75rem; font-size: 0.6875rem; color: #94a3b8; }
        .v219-chase-rates b { color: #fff; font-weight: 700; }
        .v219-result-bar {
          background: rgba(16,185,129,0.15);
          border-color: rgba(16,185,129,0.3);
        }
        .v219-main-grid {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
          margin-top: 0.75rem;
        }
        .v219-rule-strip {
          margin-top: 0.75rem;
          padding: 0.375rem 0.75rem;
          text-align: center;
          font-size: 0.625rem;
          color: #64748b;
          letter-spacing: 0.04em;
        }
        .v219-indoor-penalty { color: #f59e0b; font-weight: 600; }
        .v222-this-over {
          background: rgba(245,248,246,0.97);
          border: 1px solid rgba(16,185,129,0.15);
          border-radius: 1rem;
          padding: 0.5rem 0.75rem;
          box-shadow: 0 4px 16px rgba(0,0,0,0.18);
        }
        .v222-over-label {
          font-size: 0.625rem; font-weight: 700; color: #64748b;
          text-transform: uppercase; letter-spacing: 0.06em;
          display: block; margin-bottom: 0.375rem;
        }
        .v222-over-chips {
          display: flex;
          flex-wrap: wrap;
          gap: 0.25rem;
        }
      `}</style>
    </div>
  );
};

export default LiveScore;
